import React, { useEffect, useMemo, useState } from 'react';
import { nightingaleApi } from '@/services/api';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  Activity,
  BarChart3,
  Percent,
  RefreshCw,
  Gauge,
  ShieldAlert,
  Scale,
  Flame,
  Timer,
  Sigma,
  LineChart as LineChartIcon,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Button } from '@/components/ui/button';
import { PageHeader, SectionLabel } from '@/components/ui/page-header';
import { chartTheme, useTheme } from '@/stores/themeStore';
import {
  formatCurrency as formatCurrencyShared,
  formatNumber as formatNumberShared,
  currencySymbol,
  getMarketProfile,
} from '@/utils/format';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Analytics {
  net_returns: number;
  total_closed_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate: number;
  avg_pnl: number;
  best_trade: number;
  worst_trade: number;
  gross_profit: number;
  gross_loss: number;
  profit_factor: number;
  avg_win: number;
  avg_loss: number;
  payoff_ratio: number;
  expectancy: number;
  expectancy_r: number;
  std_dev: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  max_drawdown: number;
  max_drawdown_pct: number;
  recovery_factor: number;
  max_win_streak: number;
  max_loss_streak: number;
  avg_holding_hours: number;
  equity_curve: Array<{ i: number; cum: number; pnl: number; t: string | null }>;
}

// Small presentational card so every metric renders consistently.
const MetricCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  valueClass?: string;
}> = ({ label, value, hint, icon, valueClass = 'text-foreground' }) => (
  <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
    <div className="flex items-center justify-between mb-3 sm:mb-4">
      <div className="text-muted-foreground text-meta sm:text-body font-medium">{label}</div>
      {icon}
    </div>
    <div className={`text-display sm:text-display-lg font-mono font-bold ${valueClass}`}>{value}</div>
    {hint && <div className="mt-2 text-meta text-muted-foreground">{hint}</div>}
  </div>
);

export const HomePage: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const data = await nightingaleApi.getAnalytics();
      setAnalytics(data as Analytics);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const formatCurrency = (value: number) => {
    return formatCurrencyShared(Math.abs(value), { decimals: 2 });
  };

  const formatSigned = (value: number) =>
    `${value >= 0 ? '+' : '-'}${formatCurrency(value)}`;

  const formatNumber = (value: number) => formatNumberShared(value, 3, 0);

  const formatHolding = (hours: number) => {
    if (hours <= 0) return '—';
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  // Short axis label, e.g. "12 May". Falls back to the trade number.
  const formatEquityLabel = (p: { i: number; t: string | null }) => {
    if (!p.t) return `#${p.i}`;
    return new Date(p.t).toLocaleDateString(getMarketProfile().locale, { day: '2-digit', month: 'short' });
  };

  const equity = analytics?.equity_curve ?? [];

  const equityData = useMemo(() => {
    const positive = (analytics?.net_returns ?? 0) >= 0;
    const line = positive ? '#4ade80' : '#f87171';
    return {
      labels: equity.map(formatEquityLabel),
      datasets: [
        {
          label: 'Cumulative P&L',
          data: equity.map((p) => p.cum),
          borderColor: line,
          backgroundColor: positive ? 'rgba(74, 222, 128, 0.12)' : 'rgba(248, 113, 113, 0.12)',
          borderWidth: 2,
          fill: true,
          tension: 0.25,
          pointRadius: equity.length > 40 ? 0 : 2,
          pointHoverRadius: 4,
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics]);

  const theme = useTheme();
  const ct = chartTheme(theme);
  const equityOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const pt = equity[items[0]?.dataIndex];
              if (!pt?.t) return `Trade #${pt?.i ?? ''}`;
              return new Date(pt.t).toLocaleString(getMarketProfile().locale, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });
            },
            label: (ctx: any) => ` ${currencySymbol()}${formatNumberShared(Number(ctx.parsed.y), 3, 0)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: ct.grid(0.25) },
          ticks: { color: ct.subtle, maxTicksLimit: 8 },
        },
        y: {
          grid: { color: ct.grid(0.35) },
          ticks: {
            color: ct.subtle,
            callback: (v: any) => `${currencySymbol()}${formatNumberShared(Number(v), 3, 0)}`,
          },
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analytics, theme]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const netReturns = analytics?.net_returns || 0;
  const isProfit = netReturns >= 0;
  const sharpe = analytics?.sharpe_ratio || 0;
  const sortino = analytics?.sortino_ratio || 0;
  const profitFactor = analytics?.profit_factor || 0;
  const payoff = analytics?.payoff_ratio || 0;
  const expectancyR = analytics?.expectancy_r || 0;
  const recovery = analytics?.recovery_factor || 0;

  // Shared thresholds for "good / ok / bad" colouring of ratio metrics.
  const ratioClass = (v: number, good: number, ok: number) =>
    v >= good ? 'text-success-400' : v >= ok ? 'text-warning-400' : 'text-danger-400';

  return (
    <div>
      <PageHeader
        title="Nightingale Analytics"
        subtitle="Quantitative performance metrics from closed trades"
        actions={
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing} title="Refresh">
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : undefined} />
          </Button>
        }
      />

      {/* Primary Stats - Top Row */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <MetricCard
          label="Net Returns"
          value={formatSigned(netReturns)}
          hint={`From ${analytics?.total_closed_trades || 0} closed trades`}
          icon={isProfit ? <TrendingUp className="text-success-400" size={20} /> : <TrendingDown className="text-danger-400" size={20} />}
          valueClass={isProfit ? 'text-success-400' : 'text-danger-400'}
        />
        <MetricCard
          label="Total Trades"
          value={formatNumber(analytics?.total_closed_trades || 0)}
          hint="Closed positions"
          icon={<Activity className="text-muted-foreground" size={20} />}
        />
        <MetricCard
          label="Win Rate"
          value={`${analytics?.win_rate.toFixed(1) || '0.0'}%`}
          hint={`${analytics?.winning_trades || 0}W / ${analytics?.losing_trades || 0}L`}
          icon={<Target className="text-muted-foreground" size={20} />}
        />
        <MetricCard
          label="Avg Trade P&L"
          value={formatSigned(analytics?.avg_pnl || 0)}
          hint="Per trade average"
          icon={<BarChart3 className="text-muted-foreground" size={20} />}
          valueClass={(analytics?.avg_pnl || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}
        />
      </div>

      {/* Equity Curve */}
      <div className="bg-card border border-border rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-4">
          <LineChartIcon className="text-muted-foreground" size={18} />
          <h2 className="text-title font-semibold text-foreground">Equity Curve</h2>
          <span className="text-meta text-muted-foreground">cumulative P&L over closed trades</span>
        </div>
        {equity.length > 0 ? (
          <div className="h-64 sm:h-80">
            <Line data={equityData} options={equityOptions} />
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground text-body">No closed trades to plot yet.</div>
        )}
      </div>

      {/* Risk-Adjusted Metrics */}
      <SectionLabel>Risk-Adjusted Returns</SectionLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <MetricCard
          label="Sharpe Ratio"
          value={sharpe.toFixed(2)}
          hint="Return per unit of volatility (annualised)"
          icon={<Gauge className="text-muted-foreground" size={20} />}
          valueClass={ratioClass(sharpe, 1, 0)}
        />
        <MetricCard
          label="Sortino Ratio"
          value={sortino.toFixed(2)}
          hint="Return per unit of downside risk"
          icon={<Gauge className="text-muted-foreground" size={20} />}
          valueClass={ratioClass(sortino, 1.5, 0)}
        />
        <MetricCard
          label="Max Drawdown"
          value={`-${formatCurrency(analytics?.max_drawdown || 0)}`}
          hint={`${analytics?.max_drawdown_pct?.toFixed(1) || '0.0'}% peak-to-trough`}
          icon={<TrendingDown className="text-danger-400" size={20} />}
          valueClass="text-danger-400"
        />
        <MetricCard
          label="Recovery Factor"
          value={recovery.toFixed(2)}
          hint="Net profit / max drawdown"
          icon={<ShieldAlert className="text-muted-foreground" size={20} />}
          valueClass={ratioClass(recovery, 2, 1)}
        />
      </div>

      {/* Edge / Quality Metrics */}
      <SectionLabel>Edge & Consistency</SectionLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <MetricCard
          label="Profit Factor"
          value={profitFactor.toFixed(2)}
          hint="Gross profit / gross loss"
          icon={<Percent className="text-muted-foreground" size={20} />}
          valueClass={ratioClass(profitFactor, 1.5, 1)}
        />
        <MetricCard
          label="Payoff Ratio"
          value={payoff.toFixed(2)}
          hint="Avg win / avg loss"
          icon={<Scale className="text-muted-foreground" size={20} />}
          valueClass={ratioClass(payoff, 1, 0.5)}
        />
        <MetricCard
          label="Expectancy"
          value={`${expectancyR.toFixed(2)} R`}
          hint={`${formatSigned(analytics?.expectancy || 0)} per trade`}
          icon={<Target className="text-muted-foreground" size={20} />}
          valueClass={expectancyR >= 0 ? 'text-success-400' : 'text-danger-400'}
        />
        <MetricCard
          label="Volatility (σ)"
          value={formatCurrency(analytics?.std_dev || 0)}
          hint="Std. dev of trade P&L"
          icon={<Sigma className="text-muted-foreground" size={20} />}
        />
      </div>

      {/* Extremes & Streaks */}
      <SectionLabel>Extremes & Streaks</SectionLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <MetricCard
          label="Best Trade"
          value={`+${formatCurrency(analytics?.best_trade || 0)}`}
          hint="Largest single win"
          icon={<Award className="text-muted-foreground" size={20} />}
          valueClass="text-success-400"
        />
        <MetricCard
          label="Worst Trade"
          value={`-${formatCurrency(analytics?.worst_trade || 0)}`}
          hint="Largest single loss"
          icon={<TrendingDown className="text-danger-400" size={20} />}
          valueClass="text-danger-400"
        />
        <MetricCard
          label="Win / Loss Streak"
          value={`${analytics?.max_win_streak || 0} / ${analytics?.max_loss_streak || 0}`}
          hint="Longest consecutive run"
          icon={<Flame className="text-muted-foreground" size={20} />}
        />
        <MetricCard
          label="Avg Holding"
          value={formatHolding(analytics?.avg_holding_hours || 0)}
          hint="Entry to exit"
          icon={<Timer className="text-muted-foreground" size={20} />}
        />
      </div>

      {/* Trade Distribution - Visual Bar */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-title font-semibold text-foreground mb-4">Trade Distribution</h2>

        <div className="space-y-4">
          {/* Winning Trades Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-body text-muted-foreground">Winning Trades</span>
              <span className="text-body font-mono text-success-400">
                {analytics?.winning_trades || 0} ({analytics?.total_closed_trades ? ((analytics.winning_trades / analytics.total_closed_trades) * 100).toFixed(1) : '0.0'}%)
              </span>
            </div>
            <div className="h-8 bg-dark-800 rounded-lg overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-success-600 to-success-500 flex items-center justify-end pr-3"
                style={{
                  width: `${analytics?.total_closed_trades ? (analytics.winning_trades / analytics.total_closed_trades) * 100 : 0}%`,
                }}
              >
                {analytics && analytics.winning_trades > 0 && (
                  <span className="text-meta font-bold text-foreground">{analytics.winning_trades}</span>
                )}
              </div>
            </div>
          </div>

          {/* Losing Trades Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-body text-muted-foreground">Losing Trades</span>
              <span className="text-body font-mono text-danger-400">
                {analytics?.losing_trades || 0} ({analytics?.total_closed_trades ? ((analytics.losing_trades / analytics.total_closed_trades) * 100).toFixed(1) : '0.0'}%)
              </span>
            </div>
            <div className="h-8 bg-dark-800 rounded-lg overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-danger-600 to-danger-500 flex items-center justify-end pr-3"
                style={{
                  width: `${analytics?.total_closed_trades ? (analytics.losing_trades / analytics.total_closed_trades) * 100 : 0}%`,
                }}
              >
                {analytics && analytics.losing_trades > 0 && (
                  <span className="text-meta font-bold text-foreground">{analytics.losing_trades}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {analytics && analytics.total_closed_trades === 0 && (
          <div className="mt-6 text-center py-8">
            <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-body">
              No closed trades yet. Execute Nightingale strategy to see analytics.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
